/**
 * Contenuti della guida ai ticket per i rappresentanti
 * (`/area-associativa/guida-ticket`).
 *
 * Stessi componenti dell'admin (`SupportQueue`, `SupportTicketView`,
 * `Composer`, `ContextPanel`), ma montati da `AssociativeSupport.tsx` e
 * `AssociativeSupportDetail.tsx`: niente Posta supporto, niente "Nuovo
 * ticket", niente "Ignora mittente" ("Imposta/Cambia richiedente" solo con il
 * permesso associativa.supporto.change_requester), nessuna card del
 * richiedente e pannello dettagli sempre nel cassetto. La guida dice solo
 * quello che c'e davvero in quest'area.
 */
import type { GuideContent } from './guideModel';

const guideTicketRappresentante: GuideContent = {
  title: 'Guida ai ticket di supporto',
  intro: [
    'Da “Supporto” lavori le richieste dei canali di cui fai parte: le leggi, rispondi, le assegni e le porti a conclusione.',
    'Usa il riepilogo qui sotto per saltare alla parte che ti serve. Puoi anche stampare la pagina: il riepilogo e i bottoni spariscono, il testo e le immagini restano.',
  ],
  sections: [
    {
      id: 'introduzione',
      title: 'Che cosa trovi in “Supporto”',
      blocks: [
        {
          type: 'p',
          text: 'Ogni richiesta è un ticket con un numero (per esempio #2026-000123), un canale, uno stato e una priorità. Tutto quello che succede resta sul ticket: risposte, note interne, cambi di stato, trasferimenti.',
        },
        {
          type: 'p',
          text: 'Vedi soltanto i ticket dei canali di cui sei componente. I permessi decidono che cosa puoi fare: lettura, lavorazione (rispondere, assegnare, cambiare stato, unire) e trasferimento ad altri canali.',
        },
        {
          type: 'p',
          text: 'Alcune funzioni del Centro Ticket degli amministratori qui non ci sono, ed è normale:',
        },
        {
          type: 'ul',
          items: [
            'non c’è “Posta supporto”, cioè il registro delle email in ingresso;',
            'non puoi aprire un ticket da zero con “Nuovo ticket”;',
            'non puoi attribuire il richiedente di una email non riconosciuta;',
            'non puoi mettere un mittente nella lista di quelli ignorati;',
            'nel pannello dei dettagli non c’è la card del richiedente con il link alla scheda associato.',
          ],
        },
        {
          type: 'tip',
          text: 'Se un ticket mostra l’avviso “Richiedente da attribuire: contatta un amministratore”, la sistemazione spetta alla segreteria: fino ad allora il richiedente non riceve nessuna email. Se invece hai il permesso di cambio del richiedente, l’avviso ti offre “Imposta richiedente” e nel menu ⋯ trovi “Cambia richiedente”.',
        },
        {
          type: 'img',
          src: 'r01-coda.webp',
          alt: 'La coda dei ticket nell’area associativa, con contatori, schede, ricerca e righe',
          caption: 'La pagina Supporto: contatori, schede, filtri e la lista dei ticket.',
        },
      ],
    },
    {
      id: 'coda',
      title: 'La coda: trovare il ticket giusto',
      blocks: [
        {
          type: 'p',
          text: 'Sotto il titolo trovi i contatori. Attivi (né risolti né chiusi) e Da leggere sono cliccabili e filtrano subito la lista; In attesa, Risolti nel mese e 1ª risposta (media) sono solo informativi.',
        },
        {
          type: 'p',
          text: 'Le schede sono Aperti e in lavorazione, Non assegnati, Presi da me, Invitato e Risolti e chiusi; se un altro canale ha messo in copia il tuo, compare anche “In copia”. La lista parte da “Aperti e in lavorazione”, la prima a sinistra: tutti i ticket non ancora risolti o chiusi dei tuoi canali. In “Invitato” trovi i ticket di altri canali in cui sei stato invitato come partecipante; in “In copia” quelli di altri canali condivisi con il tuo.',
        },
        {
          type: 'p',
          text: 'Il bottone con l’imbuto apre i filtri: stato, priorità e canale. Lo stato parte da “Attivi (non risolti/chiusi)”: è il motivo per cui i ticket risolti non li vedi finché non cambi filtro. Il numero sul bottone dice quanti filtri sono accesi.',
        },
        {
          type: 'p',
          text: 'La ricerca trova anche parti di parola (es. “Rizz”), senza badare a maiuscole e accenti: numero, oggetto, nominativo, codice, partita IVA ed email del richiedente, più le parole intere dei messaggi; con più parole devono comparire tutte. Se non hai scelto uno stato nei Filtri, la ricerca guarda in tutti gli stati (anche risolti e chiusi), con i ticket ancora aperti in cima. Nell’elenco a tabella clicchi l’intestazione di una colonna (Ticket, Data, Richiedente, Canale, Stato, Priorità, Attività) per ordinare tutta la coda; un secondo clic inverte, un terzo torna all’ordine di sempre. Il bottone con le frecce alterna l’ordinamento della pagina fra priorità e non letti (con una colonna scelta la toglie). Il segnalibro apre le viste salvate: dai un nome ai filtri che usi sempre e li ritrovi con un clic.',
        },
        { type: 'p', text: 'Ogni riga ti dice, dall’alto in basso:' },
        {
          type: 'ul',
          items: [
            'la barra colorata a sinistra indica la priorità;',
            'il pallino verde e l’etichetta “Nuovo messaggio” segnalano un messaggio del richiedente non ancora letto;',
            'il pallino ambra e l’etichetta “Nota da leggere” segnalano una nota interna di un collega che tu non hai ancora aperto;',
            'il chip SLA dice quanto tempo resta (o da quanto è sforato); sui ticket risolti e chiusi non compare;',
            'numero del ticket e nominativo del richiedente;',
            'stato, eventuale valutazione, priorità, origine, tipo di richiedente, canale e tag;',
            'a destra le foto o le iniziali di chi ci sta lavorando (fino a tre, poi “+N”).',
          ],
        },
        {
          type: 'tip',
          text: 'I contatori cliccabili e il filtro Stato sono la stessa cosa: premere “Attivi” imposta il filtro stato su “Attivi”. Gli altri filtri restano come li hai lasciati.',
        },
        {
          type: 'img',
          src: 'r01-coda.webp',
          alt: 'Dettaglio della coda con contatori, schede, ricerca e filtri',
          caption: 'Contatori, schede e filtri: da qui si restringe la lista.',
        },
      ],
    },
    {
      id: 'scheda',
      title: 'La scheda del ticket',
      blocks: [
        {
          type: 'p',
          text: 'Aprendo una riga si cambia pagina: il ticket occupa tutto lo spazio e in alto a sinistra trovi sempre “Torna alla coda”.',
        },
        {
          type: 'p',
          text: 'In cima ci sono numero, stato, priorità, origine e SLA, poi l’oggetto e una riga con canale, richiedente, assegnatari e data di apertura.',
        },
        { type: 'p', text: 'A destra la barra delle azioni:' },
        {
          type: 'ul',
          items: [
            '“Prendi in carico”: ti aggiunge fra gli assegnatari. Se il ticket è già tuo il bottone resta visibile ma spento.',
            '“Rispondi”: porta il cursore nella casella di scrittura.',
            '“Risolvi”: compare quando lo stato attuale permette di passare a Risolto.',
            '“Condividi”: copia il link al ticket (indirizzo /t/ col numero) o lo manda su WhatsApp. Il link non fa entrare nessuno: chi lo apre accede con il proprio profilo e vede il ticket solo se rientra nei suoi canali.',
            '“Condividi via email” (sempre dal bottone Condividi): apre una finestra in cui scegli cosa mandare — solo il messaggio iniziale, solo l’ultimo o tutta la conversazione, con o senza le note interne (spente di serie) e i dettagli del ticket — vedi l’anteprima e, da “Apri con”, lo apri nel programma di posta predefinito del computer, in Outlook sul web (account di lavoro o scuola, oppure Outlook.com per gli account personali) o in Gmail in una nuova scheda (l’ultima scelta resta ricordata nel browser), oppure copi il testo con “Copia testo”. Se il testo è troppo lungo per la posta nel browser, viene copiato negli appunti e il messaggio si apre con il solo oggetto: incollalo nel corpo. Se i messaggi scelti hanno allegati, la casella “Includi link agli allegati” (accesa di serie) aggiunge accanto a ogni file un link per scaricarlo senza accedere alla piattaforma, valido per il periodo indicato nella finestra; il link si crea solo quando apri il programma di posta o copi il testo (nell’anteprima vedi un segnaposto): chi riceve l’email lo può inoltrare, quindi spegnila per i documenti riservati; la condivisione resta nella cronologia del ticket.',
            '“Stampa” (sempre dal bottone Condividi): scegli “PDF (scarica il file)” o “Stampante”, se includere gli allegati e, se le vedi, le note interne (accese di serie). Il documento ha intestazione, dati del ticket e tutta la conversazione in ordine cronologico, con le note marcate “Nota interna”; con gli allegati, le immagini compaiono sotto il loro messaggio, i PDF in fondo al documento (ognuno preceduto da una pagina che dice da quale messaggio viene) e gli altri file solo con nome e dimensione; i PDF protetti da password o illeggibili restano elencati come “non incorporabile”. Oltre 25 MB di allegati o 100 pagine di PDF il resto viene solo elencato. Se il browser non apre la stampa, il PDF si apre in una nuova scheda: usa “Stampa” del browser.',
            '“Dettagli”: apre il cassetto laterale con proprietà e contesto.',
            'Il menu ⋯ contiene “Cambia stato / priorità”, “Trasferisci a un gruppo” (se hai il permesso), “Aggiungi componente”, “Unisci a un altro ticket”, “Cambia richiedente” (solo con il permesso dedicato) e, in fondo e in rosso, “Chiudi ticket” (solo con il permesso di chiusura diretta).',
          ],
        },
        {
          type: 'p',
          text: 'Il cassetto “Dettagli” sta sempre nel pannello laterale, a qualunque larghezza di schermo: in quest’area la pagina non è larga abbastanza per tenerlo in colonna. Dentro trovi Proprietà, Email originale sui ticket nati da posta, Trasferimenti, Cronologia e Altri ticket dell’associato.',
        },
        {
          type: 'p',
          text: 'Nella card Proprietà puoi cambiare stato (scegli la destinazione e premi “Applica”), canale e assegnatari. Priorità, origine e tag sono in sola lettura: la priorità e i tag li gestisce la segreteria dal Centro Ticket.',
        },
        {
          type: 'p',
          text: 'Sui ticket del canale “Documento Unico” sotto le Proprietà c’è la card “Dati della pratica” (targa, tipo formalità, numero ticket ACI / DTT, facoltativi, scritti dall’associato all’apertura): ogni valore si copia con un clic e con “Modifica” lo correggi, salvo sui ticket chiusi.',
        },
        {
          type: 'p',
          text: 'Se un collega sta guardando lo stesso ticket vedi “Anche qui:” con il suo nome o la sua foto. Mentre scrive compare “… sta scrivendo”.',
        },
        {
          type: 'warn',
          text: 'Mentre un’operazione è in corso i bottoni restano al loro posto ma non rispondono. Se nel frattempo un collega ha cambiato il ticket, ricevi l’avviso “Il ticket è cambiato” e i dati si ricaricano da soli: rileggi prima di rifare l’azione.',
        },
        {
          type: 'img',
          src: 'r02-scheda.webp',
          alt: 'Scheda di un ticket nell’area associativa, con “Torna alla coda”, intestazione e conversazione',
          caption: 'La scheda del ticket: intestazione, azioni e conversazione.',
        },
        {
          type: 'img',
          src: 'r03-dettagli.webp',
          alt: 'Cassetto “Dettagli” aperto con le card Proprietà, Trasferimenti e Cronologia',
          caption: 'Il cassetto “Dettagli”: qui si cambiano stato, priorità, canale, assegnatari e tag.',
        },
      ],
    },
    {
      id: 'rispondere',
      title: 'Rispondere e prendere appunti',
      blocks: [
        {
          type: 'p',
          text: 'In fondo alla conversazione c’è una sola casella di scrittura con due modalità: “Risposta all’associato” e “Nota interna”. La nota interna diventa ambra ed è visibile solo a chi lavora nel canale: non viene mai inviata al richiedente.',
        },
        {
          type: 'p',
          text: 'Dal canale di smistamento (il canale predefinito) non si risponde all’associato, nemmeno dall’app: al posto della risposta compare l’avviso «Ticket ancora da smistare» con il bottone “Trasferisci a un gruppo”. Trasferisci prima il ticket al canale competente; intanto puoi scrivere note interne, menzionare i colleghi e chiedere all’assistente “Riassumi il ticket”.',
        },
        {
          type: 'p',
          text: 'Quando scrivi una nota interna, per i componenti del canale e per gli assegnatari del ticket (tu escluso) il ticket diventa “da leggere”: in coda compaiono il pallino ambra e “Nota da leggere”. Alla prima apertura del ticket le note nuove hanno il bordo ambra più marcato e il chip “Nuova”, che restano finché non passi a un altro ticket. Non parte nessuna email e nessuna notifica, salvo per i colleghi che menzioni.',
        },
        {
          type: 'tip',
          title: 'Menzionare un collega nelle note',
          text: 'Nella nota interna scrivi @ e si apre l’elenco dei colleghi che puoi menzionare: i componenti del canale e gli assegnati esterni al canale (tu escluso), con foto e ruolo. Continua a scrivere per filtrare, scegli con le frecce e Invio (Esc chiude l’elenco) o con un clic: nel testo compare @Nome Cognome e sopra il bottone “Salva nota” il chip del menzionato, con “Verrà avvisato per email”. Il menzionato riceve un’email con il testo della nota secondo le sue preferenze (subito, fascia oraria o riepilogo; nessuna se ha scelto “nessuna email”) e, se il responsabile del canale ha attivato “Invia subito l’email a chi viene menzionato”, la riceve subito. Per lui la nota resta “da leggere” e nel thread ha il segno “Ti ha menzionato”; i nomi menzionati sono evidenziati. Se cancelli @Nome dal testo o togli il chip, la menzione non parte. Nella risposta all’associato le menzioni non esistono.',
        },
        {
          type: 'tip',
          title: 'Invitare chi non è del canale',
          text: 'In fondo all’elenco che si apre con @ c’è la voce “Invita rappresentanti o esterni…”: apre una ricerca per nome, studio, partita IVA, email, carica o provincia. La persona scelta entra nel testo come menzione e, al salvataggio della nota, diventa partecipante del ticket e riceve l’email della menzione. Puoi invitare anche dalle Proprietà, sezione “Partecipanti” (bottone “Invita…”), e da lì togliere un partecipante: perde subito l’accesso (salvo gli amministratori che vedono tutti i canali). Nella ricerca trovi anche gli amministratori della segreteria che vedono tutti i canali: invitandoli ricevono l’avviso e compaiono tra i partecipanti (mantengono i loro diritti e non vedono il banner “Sei stato invitato in questo ticket”). Chi viene invitato vede il banner “Sei stato invitato in questo ticket”: legge la conversazione, scrive note interne e propone risposte che il canale approva; non cambia stato, non trasferisce, non chiude e non invita altri. Nell’area rappresentante serve il permesso “Invitare nei ticket” assegnato alla carica; i ticket in cui sei invitato li trovi nella scheda “Invitato” della coda.',
        },
        {
          type: 'p',
          text: 'Le due bozze sono separate: se passi da risposta a nota e torni indietro, ritrovi quello che avevi scritto. Nella risposta puoi inviare con Ctrl+Invio (nella nota no).',
        },
        {
          type: 'p',
          text: '“Risposte rapide” compare solo se il canale ha dei testi preimpostati. Il testo scelto si aggiunge in coda a quello che hai già scritto, e le variabili {nominativo}, {ticket}, {operatore} e {canale} vengono sostituite con i valori del ticket.',
        },
        {
          type: 'p',
          text: '“Assistente AI” propone quattro rifiniture del testo che hai scritto: Migliora, Correggi, Più formale, Sintetizza. La proposta compare sopra la casella e non sostituisce mai la tua bozza da sola: scegli tu fra “Annulla” e “Usa”. Se il servizio non è disponibile il bottone sparisce per il resto della sessione.',
        },
        {
          type: 'p',
          text: 'Nello stesso menu ci sono due azioni che leggono l’intero ticket (conversazione, note interne e risposte predefinite del canale). “Proponi una risposta” scrive una bozza di risposta al richiedente; se hai già iniziato a scrivere, parte dalla tua bozza e la completa. Non compare sui ticket chiusi e nella nota interna. “Riassumi il ticket” prepara un riepilogo in punti (chi, cosa chiede, cosa è stato fatto, cosa manca, prossimo passo) e lo propone sempre nella nota interna, anche se eri in modalità risposta: con “Usa” si aggiunge in coda alla nota già scritta. In entrambi i casi nulla viene inviato finché non lo fai tu.',
        },
        {
          type: 'warn',
          title: 'Assistente AI: rileggi sempre',
          text: 'Per queste azioni il contenuto del ticket, comprese le note interne e i dati del richiedente, viene inviato a un servizio esterno (OpenAI) per l’elaborazione. La proposta può contenere errori o imprecisioni: rileggila e correggila sempre prima di usarla.',
        },
        {
          type: 'p',
          text: 'Con “Allega” aggiungi fino a 5 file da 10 MB l’uno. Sono ammessi PDF, immagini JPEG, PNG e WebP, documenti Word ed Excel.',
        },
        {
          type: 'p',
          text: 'Dopo “Invia risposta” (o “Invia per approvazione”) si apre il popup di conferma, dove scegli come resta il ticket: “Invia lasciando il ticket …” oppure uno degli stati raggiungibili da quello attuale (per esempio “Invia e metti in attesa di risposta dell’associato” o “Invia come risolto”). La scelta è obbligatoria: nessuna voce è preselezionata e il tasto di conferma resta spento finché non scegli.',
        },
        {
          type: 'tip',
          text: 'Con “Invia come risolto” il richiedente riceve una sola email, quella della risoluzione. Se invece invii la risposta e poi cambi stato a parte, le email diventano due.',
        },
        {
          type: 'p',
          text: 'Correggere un messaggio: se hai il permesso, accanto alla data di ogni messaggio della conversazione compare una matita. Il clic trasforma la bolla in una casella di testo: correggi, poi “Salva” (anche con Ctrl+Invio) o “Annulla” (anche con Esc). I permessi sono due e separati: uno per i messaggi del richiedente e uno per quelli del canale, compresi i tuoi. Chi ha solo la lavorazione non modifica nulla, nemmeno i propri messaggi. Con uno qualsiasi dei due permessi puoi correggere anche l’oggetto del ticket: la matita accanto all’oggetto, in cima, apre una casella in linea (“Salva” o Invio, “Annulla” o Esc). Non parte nessuna email: le email successive useranno il nuovo oggetto e la cronologia registra chi l’ha cambiato e com’era prima.',
        },
        {
          type: 'warn',
          title: 'Attenzione',
          text: 'La correzione vale per la piattaforma: l’email gia inviata al richiedente NON cambia, e nessun avviso parte per la modifica. Accanto alla data resta scritto “modificato” (il richiedente lo vede anche lui) e la cronologia registra chi ha corretto e quando; il testo originale resta conservato. Sui ticket chiusi la matita non compare. Con lo stesso permesso togli anche i singoli allegati di quel messaggio (per esempio le immagini di firma arrivate con le email inoltrate): la × accanto al nome dell’allegato chiede conferma, la rimozione è definitiva e non avvisa nessuno; l’allegato sparisce dalla conversazione per tutti, senza lasciare segnaposto, e la cronologia registra chi l’ha tolto.',
        },
        {
          type: 'p',
          text: 'Correggere o eliminare una nota interna: passando sulla nota (o con il tasto Tab; sul sito da telefono o tablet sono sempre visibili; nell’app non ci sono ancora) compaiono la matita e il cestino. La matita apre la nota in una casella di testo (“Salva” anche con Ctrl+Invio, “Annulla” anche con Esc); il cestino chiede conferma. Ognuno corregge o elimina le PROPRIE note entro 15 minuti da quando le ha scritte, anche da partecipante invitato o da canale in copia. Dopo i 15 minuti, e per le note degli altri, serve il permesso “Modificare ed eliminare qualsiasi nota interna” (associativa.supporto.edit_internal_notes, lo assegna la segreteria alla carica), che vale solo sui ticket che lavori: chi è invitato o in copia, anche con il permesso, tocca solo le proprie note entro i 15 minuti.',
        },
        {
          type: 'tip',
          title: 'Da sapere',
          text: 'Nessun avviso parte per la modifica o l’eliminazione: le @menzioni aggiunte correggendo non avvisano e non invitano nessuno. I riepiloghi email non ancora partiti non riportano le note eliminate e mostrano il testo corretto di quelle modificate; quelli già inviati, come le email di menzione già partite, restano come sono. La nota corretta mostra “modificata da … · data”; quella eliminata sparisce dalla conversazione, senza lasciare segnaposto, e i suoi allegati non si scaricano più. La cronologia registra chi ha corretto o eliminato e quando. Sui ticket chiusi matita e cestino non compaiono.',
        },
        {
          type: 'tip',
          title: 'Da sapere',
          text: 'Una risposta su un ticket “Aperto” o “Preso in carico” lo porta da sola a “In lavorazione”: non devi cambiarlo a mano.',
        },
      ],
    },
    {
      id: 'stati',
      title: 'Gli stati del ticket',
      blocks: [
        { type: 'p', text: 'Gli stati sono sei:' },
        {
          type: 'ul',
          items: [
            'Aperto: la richiesta è arrivata e nessuno l’ha ancora presa.',
            'Preso in carico: qualcuno se n’è fatto carico.',
            'In lavorazione: ci si sta lavorando.',
            'In attesa associato: la palla è al richiedente; il conteggio dello SLA di risoluzione si mette in pausa e il chip diventa “SLA in pausa”.',
            'Risolto: è la tua dichiarazione che la pratica è conclusa. Il richiedente può riaprirla entro 14 giorni.',
            'Chiuso: definitivo, non si riapre.',
          ],
        },
        { type: 'p', text: 'I passaggi ammessi sono questi:' },
        {
          type: 'ul',
          items: [
            'da Aperto: Preso in carico, In lavorazione;',
            'da Preso in carico: In lavorazione, In attesa associato, Risolto;',
            'da In lavorazione: In attesa associato, Risolto;',
            'da In attesa associato: In lavorazione, Risolto;',
            'da Risolto: In lavorazione;',
            'da Chiuso: nessuno.',
          ],
        },
        {
          type: 'warn',
          text: '“Chiuso” non compare nel “Cambia stato”. Ci si arriva con la chiusura automatica dopo 14 giorni da “Risolto”, con l’azione “Non pertinente” della segreteria, unendo il ticket a un altro (quello assorbito viene chiuso) oppure con “Chiudi ticket” dal menu ⋯, se hai il permesso dedicato. Nell’elenco e nella scheda un ticket chiuso perché unito a un altro si vede come “Unito” e uno chiuso con “Non pertinente: ignora mittente” come “Indesiderato” (lo stato salvato resta Chiuso); nei Filtri “Nascondi uniti e indesiderati” li toglie dalla coda.',
        },
        {
          type: 'p',
          text: '“Chiudi ticket” chiude subito, da qualunque stato, ed è silenziosa: il richiedente non riceve né email né notifiche. Il motivo facoltativo resta solo nella cronologia interna. Un ticket chiuso non si riapre. Sul ticket chiuso la casella di scrittura sparisce: al suo posto una riga avvisa che non si può più rispondere né aggiungere note interne. Su “Risolto”, invece, si scrive come sempre.',
        },
        {
          type: 'p',
          text: 'La chiusura automatica gira ogni notte alle 03:15 ed è silenziosa: nessuna email al richiedente, nessuna notifica agli operatori. Nella cronologia del ticket resta la voce “Chiusa automaticamente”.',
        },
        {
          type: 'p',
          text: 'Il chip SLA cambia colore man mano che il tempo scade: verde quando c’è margine, ambra nell’ultimo quarto, rosso quando è sforato. Passandoci sopra leggi la data di scadenza.',
        },
      ],
    },
    {
      id: 'assegnare',
      title: 'Assegnare, trasferire, unire',
      blocks: [
        {
          type: 'p',
          text: '“Prendi in carico” aggiunge te fra gli assegnatari. Non toglie il ticket agli altri: su un ticket possono lavorare più persone. Se rispondi all’associato senza averlo preso in carico, vieni aggiunto automaticamente fra chi segue il ticket (con una bozza, quando viene approvata), senza email aggiuntive; la cronologia lo segna come “Preso in carico (automatico)”. Le note interne non prendono in carico.',
        },
        {
          type: 'p',
          text: '“Aggiungi componente” apre l’elenco di chi può lavorare su quel canale: spunti chi vuoi coinvolgere. Dal cassetto “Dettagli”, nella card Proprietà, puoi anche togliere un assegnatario cliccando sulla sua pillola.',
        },
        {
          type: 'p',
          text: 'Un amministratore può assegnarti un ticket di un canale di cui non fai parte, perché serve la tua competenza. Lo trovi nella coda (“Presi da me” e “Tutti”) e, aprendolo, un avviso ti ricorda che sei assegnato come esterno al canale. Se è l’unico ticket che hai, la voce Supporto compare lo stesso nel menu.',
        },
        {
          type: 'tip',
          text: 'Da esterno vedi solo quel ticket: puoi rispondere, scrivere note e cambiare lo stato, ma non trasferire, unire, chiudere, cambiare il richiedente o gli assegnatari. Se vieni tolto dagli assegnatari, o il ticket viene trasferito, non lo vedi più.',
        },
        {
          type: 'p',
          text: '“Trasferisci a un gruppo” sposta il ticket su un altro canale e richiede un motivo. Serve il permesso di trasferimento: senza, la voce non compare. Lo stesso vale se cambi il canale dalla card Proprietà: è un trasferimento e chiede comunque il motivo.',
        },
        {
          type: 'p',
          text: 'Nel trasferimento puoi spuntare “Anche in copia a”: quei canali vedono il ticket senza diventarne responsabili (si aggiungono a quelli già in copia, fino a 10). Dal cassetto “Dettagli”, nella card Proprietà, la riga “In copia” elenca i canali condivisi: con il permesso di trasferimento li aggiungi dal menu “Aggiungi…”, li togli (la X sul nome) o ne rendi uno responsabile (“Rendi responsabile”): gli assegnatari si azzerano, il nuovo canale riceve l’avviso e il tuo resta in copia. Il canale di smistamento non va mai in copia: su un ticket ancora da smistare “Rendi responsabile” apre il trasferimento.',
        },
        {
          type: 'tip',
          title: 'Quando il ticket è in copia al tuo canale',
          text: 'Ricevi l’avviso “Ticket condiviso con il vostro canale” (secondo le tue preferenze email), lo trovi in “In copia” e, aprendolo, l’avviso “Ticket condiviso in copia con il tuo canale” ti ricorda che il responsabile è un altro canale. Puoi leggere, scrivere note interne e menzionare i colleghi; le tue risposte all’associato diventano bozze che il canale responsabile approva. Stato, trasferimento, unione, assegnatari e link di condivisione degli allegati restano al responsabile, e non hai il pallino “da rispondere”: solo le note da leggere. Se il tuo canale viene tolto dalla copia, il ticket sparisce dalla tua coda, anche se lo avevi aperto.',
        },
        {
          type: 'p',
          text: '“Unisci a un altro ticket” serve quando la stessa persona ha aperto due volte la stessa richiesta. Ti vengono proposti solo ticket dello stesso richiedente; a unione fatta vieni portato sul ticket superstite.',
        },
        {
          type: 'warn',
          text: 'L’unione non si annulla. Il ticket assorbito viene chiuso e da lì in poi mostra solo un avviso con il link al ticket superstite.',
        },
      ],
    },
    {
      id: 'inoltrare',
      title: 'Ti scrivono per email? Inoltra al supporto',
      blocks: [
        {
          type: 'p',
          text: 'Finché gli associati scriveranno anche alla tua casella personale, e non solo dal modulo, la regola è una: la richiesta non si lavora nella posta, si inoltra alla casella di supporto e diventa un ticket. Così resta tracciata, la vede tutto il canale e le risposte partono dal Centro Ticket con il tag [#numero].',
        },
        {
          type: 'ol',
          items: [
            'Apri l’email dell’associato nel tuo programma di posta (Outlook, Thunderbird, Gmail).',
            'Inoltrala a supporto@studi.unasca.it. Va bene l’inoltro normale nel corpo del messaggio oppure l’inoltro come allegato: il sistema li riconosce entrambi.',
            'Non cambiare l’oggetto e non cancellare il blocco “Da: / Inviato: / Oggetto:” dell’originale: è da lì che il sistema legge chi è il vero richiedente.',
            'Se vuoi, scrivi una nota sopra l’inoltro (per esempio “chiede il duplicato della ricevuta”): la nota finisce nel ticket come nota interna, non la vede l’associato.',
            'Entro pochi minuti il ticket compare nella coda del canale “Segreteria nazionale”, con il richiedente e la sua email già impostati.',
          ],
        },
        {
          type: 'tip',
          title: 'Il richiedente non è nel blocco inoltrato',
          text: 'Se hai riscritto la richiesta a mano o l’originale non ha un’intestazione riconoscibile, aggiungi nel corpo una riga “Richiedente: nome <email>”: il sistema la usa al posto dell’intestazione. In mancanza, il ticket nasce con l’avviso “Richiedente da attribuire” e un collega con i permessi lo completa a mano.',
        },
        {
          type: 'warn',
          text: 'Non rispondere all’associato dalla tua posta e poi inoltrare: la tua risposta verrebbe letta come una citazione e non come la richiesta. Prima inoltri, poi rispondi dal ticket.',
        },
        {
          type: 'p',
          text: 'Gli allegati dell’email originale (PDF, foto, documenti) vengono portati nel ticket. L’oggetto del ticket è quello dell’email, senza i prefissi “I:”, “Fwd:” o “R:”.',
        },
        {
          type: 'p',
          text: 'Se l’associato risponde alla tua casella invece che al ticket, inoltra anche quella risposta: se l’oggetto conserva il tag [#numero] il messaggio si aggancia al ticket esistente, altrimenti apre un ticket nuovo che puoi unire al primo.',
        },
      ],
    },
    {
      id: 'associato',
      title: 'Che cosa vede chi ha aperto la richiesta',
      blocks: [
        {
          type: 'p',
          text: 'L’associato segue la sua richiesta dall’area riservata. Vede l’elenco delle sue pratiche con tre contatori (in corso, da leggere, concluse), le pillole per filtrare e la ricerca per numero o oggetto.',
        },
        {
          type: 'p',
          text: 'Nel dettaglio trova la linea del tempo della pratica (Aperta, Presa in carico, eventuali passaggi ad altro gruppo, In lavorazione, Risolta, Chiusa) e, quando serve, l’avviso “Tocca a te”: compare se lo stato è “In attesa associato” oppure se c’è una risposta che non ha ancora letto.',
        },
        {
          type: 'p',
          text: 'La cronologia è a bolle: da una parte lui, dall’altra il canale con il logo UNASCA. Con “Aggiungi comunicazione” può scrivere e allegare file, ma il riquadro non è mai aperto di default e se l’ultimo messaggio è già suo gli viene chiesta conferma. Su una pratica risolta o chiusa il riquadro non c’è.',
        },
        {
          type: 'p',
          text: 'Da “Risolto” può riaprire entro 14 giorni, con una conferma che gli ricorda di farlo solo se il problema non era davvero risolto. A pratica conclusa può lasciare una valutazione da 1 a 5 stelle con un commento: la stellina compare poi nella riga di coda.',
        },
        {
          type: 'warn',
          text: 'Il richiedente dialoga con il canale, non con te: i messaggi degli operatori gli arrivano firmati con il nome del canale, senza nomi né foto. Non vede le note interne, gli assegnatari, lo SLA e i motivi dei trasferimenti.',
        },
        {
          type: 'img',
          src: '14-associato-dettaglio.webp',
          alt: 'Dettaglio della richiesta come lo vede l’associato: linea del tempo, avviso “Tocca a te” e cronologia a bolle',
          caption: 'La stessa pratica vista dall’associato.',
        },
      ],
    },
    {
      id: 'profilo',
      title: 'La tua foto e il tema',
      blocks: [
        {
          type: 'p',
          text: 'La foto profilo si carica dalla Dashboard dell’area associativa, nella card “Profilo”: JPEG, PNG o WebP fino a 2 MB. Senza foto resti rappresentato dalle tue iniziali.',
        },
        {
          type: 'p',
          text: 'La foto compare accanto alle tue risposte nella conversazione, fra gli assegnatari nella riga di coda e nella card Proprietà, e nell’avviso “Anche qui:” quando stai guardando un ticket insieme a un collega.',
        },
        {
          type: 'p',
          text: 'Il bottone con il sole o la luna nella barra in alto passa fra tema chiaro e scuro. La scelta resta salvata sul tuo browser.',
        },
      ],
    },
    {
      id: 'notifiche',
      title: 'Le tue notifiche email',
      blocks: [
        {
          type: 'p',
          text: 'Scegli come ricevere le email dei ticket da “Notifiche email”, il link accanto a “Guida all’uso” sopra la coda dei ticket. Vale solo per te: ogni operatore ha le sue preferenze.',
        },
        {
          type: 'ul',
          items: [
            'Subito: un’email per ogni evento (nuovo ticket, risposta del richiedente, assegnazione, trasferimento, SLA), come finora. È la scelta predefinita.',
            'Solo in fascia oraria: subito dentro la fascia e nei giorni scelti; fuori fascia gli eventi si raccolgono in un riepilogo che arriva alla riapertura.',
            'Riepilogo giornaliero: un’unica email al giorno, all’ora scelta, con gli eventi divisi per canale e per tipo, ognuno con il link al ticket.',
            'Riepilogo settimanale: come il giornaliero, una volta alla settimana nel giorno e all’ora scelti.',
            'Nessuna email: niente email dei ticket, nemmeno per gli eventi urgenti.',
          ],
        },
        {
          type: 'tip',
          title: 'Eventi urgenti',
          text: 'Con la casella “Gli eventi urgenti arrivano subito comunque” attiva, i ticket con priorità urgente, quelli assegnati o trasferiti a te e gli SLA sforati arrivano subito anche con la fascia oraria o il riepilogo.',
        },
        {
          type: 'p',
          text: 'Nei riepiloghi giornaliero e settimanale trovi anche la sezione “Note interne da leggere”: le note dei colleghi che non hai ancora aperto. Se nel frattempo hai già letto il ticket, la nota non compare. Con “Subito” e con la fascia oraria le note interne non mandano email, come prima.',
        },
        {
          type: 'p',
          text: '“Invia email di prova” ti manda subito un riepilogo con dati di esempio, per vedere come arriva (massimo 3 all’ora).',
        },
        {
          type: 'p',
          text: 'Il link “Apri” nelle email dei ticket ti porta direttamente al ticket, già dentro, senza fare il login: vale 72 ore e solo finché hai accesso a quel ticket. Se il link è scaduto ti chiediamo di accedere e poi ti portiamo comunque al ticket.',
        },
        {
          type: 'warn',
          text: 'Non inoltrare le email dei ticket: chi apre il link entra con il tuo profilo. Se l’hai fatto per errore, usa “Invalida i link già inviati” in fondo alla pagina Notifiche email: i link già ricevuti smettono subito di funzionare. Le sessioni già aperte, invece, restano valide fino alla loro scadenza (7 giorni): se qualcuno è già entrato con il tuo profilo, avvisa l’amministratore.',
        },
        {
          type: 'warn',
          text: 'Le notifiche nel centro notifiche e i pallini nella coda restano immediate. Le email agli associati e agli ospiti non cambiano.',
        },
      ],
    },
    {
      id: 'conoscenza',
      title: 'La base di conoscenza',
      blocks: [
        {
          type: 'p',
          text: 'La base di conoscenza raccoglie i casi già risolti dal canale, riscritti in forma generica: titolo (la domanda in breve), domanda, risposta e tag. Serve all’assistente: quando usi “Proponi una risposta”, le schede approvate più simili alla richiesta vengono passate all’assistente come esempi.',
        },
        {
          type: 'ul',
          items: [
            'Le schede nascono da sole come BOZZE quando un ticket a cui il canale ha risposto passa a “Risolto” (o viene chiuso): le scrive l’assistente AI togliendo nomi, email, numeri di pratica e altri dati personali.',
            'Una bozza non viene mai usata finché qualcuno non la approva. Prima di approvare rileggila: correggi il testo e controlla che non siano rimasti dati personali.',
            'Dalla scheda puoi salvare le correzioni, approvare, archiviare (la scheda esce dalle ricerche ma resta consultabile), ripristinare un’archiviata come bozza e, per le bozze nate da un ticket, “Rigenera dal ticket”.',
            'Puoi anche scrivere una scheda a mano con “Nuova scheda”, come bozza o già approvata.',
            'Dal menu ⋯ di un ticket, “Crea scheda di conoscenza da questo ticket” prepara subito la bozza, senza aspettare la risoluzione.',
          ],
        },
        {
          type: 'p',
          text: 'Sotto la proposta di “Proponi una risposta” compare l’elenco “Casi simili usati”, con il link alla scheda e, se puoi aprirlo, al ticket di origine. Quando premi “Usa” la scheda conta un utilizzo: nella base di conoscenza vedi quante volte è servita.',
        },
        {
          type: 'tip',
          title: 'Dove si trova',
          text: 'Dalla lista dei ticket, con il link “Base di conoscenza” accanto a Guida all’uso e Notifiche email. Vedi solo le schede dei canali di cui sei componente.',
        },
        {
          type: 'warn',
          title: 'Permessi',
          text: 'Consultare la base di conoscenza e gestirla (correggere, approvare, archiviare, creare schede e generarle dai ticket) sono due permessi distinti dell’area associativa. I rappresentanti non eliminano le schede: le archiviano. “Proponi una risposta” usa le schede approvate anche senza questi permessi.',
        },
      ],
    },
    {
      id: 'bozze',
      title: 'Risposte in bozza e responsabile canale',
      blocks: [
        {
          type: 'p',
          text: 'In alcuni canali il responsabile può decidere che le risposte di un componente passino prima da lui. Se per te è così, nel composer il bottone diventa “Invia per approvazione” e sotto compare l’avviso “La risposta sarà inviata all’associato dopo l’approvazione del responsabile del canale”. Le note interne funzionano come sempre.',
        },
        {
          type: 'ul',
          items: [
            'La tua risposta compare nella conversazione come card gialla tratteggiata “Bozza in attesa di approvazione”: il richiedente non la vede e non riceve nulla finché non viene approvata.',
            'Finché è in attesa puoi correggerla (“Modifica”) o ritirarla (“Ritira”).',
            'Se viene approvata parte a tuo nome, eventualmente corretta dal responsabile; se viene rifiutata vedi il motivo sotto la card e ricevi un’email con l’esito.',
            'Lo stato che scegli nel popup di invio (per esempio “Invia come risolto”) cambia quando la bozza viene approvata, non subito.',
          ],
        },
        {
          type: 'p',
          text: 'Anche chi è stato invitato nel ticket risponde solo con bozze. Quando una sua bozza viene approvata, la risposta parte a suo nome ma lui non diventa assegnatario: non è componente del canale.',
        },
        {
          type: 'p',
          text: 'Se sei responsabile del canale (permesso “Responsabile canale”):',
        },
        {
          type: 'ul',
          items: [
            'Sulla card della bozza trovi “Approva e invia”, “Correggi e approva” (modifichi il testo e poi “Approva con modifiche”) e “Rifiuta” (il motivo è obbligatorio e arriva all’autore).',
            'In lista le righe con bozze hanno il badge “N bozze”; il filtro rapido “Bozze da approvare” e il contatore “BOZZE” ti mostrano subito i ticket da guardare.',
            'Dall’ingranaggio “Impostazioni canali” nella barra (o dal link sopra la lista) scegli, per ogni componente dei tuoi canali, se “Può rispondere all’associato”. Se lo togli a te stesso, le tue risposte diventano bozze anche per te.',
          ],
        },
        {
          type: 'tip',
          title: 'Ticket chiuso',
          text: 'Su un ticket chiuso la bozza non si può più approvare né correggere: si può solo rifiutare o ritirare.',
        },
        {
          type: 'p',
          text: 'Lo stesso vale per i componenti di un canale in copia: propongono bozze, le approva sempre il canale responsabile, mai quello in copia.',
        },
      ],
    },
    {
      id: 'impostazioni-canale',
      title: 'Impostazioni del canale',
      blocks: [
        {
          type: 'p',
          text: 'Se sei responsabile del canale (permesso “Responsabile canale”), per ogni canale di cui sei componente puoi accendere alcuni interruttori di notifica e visualizzazione. Partono tutti spenti: finché restano spenti il canale si comporta come prima. Anche la segreteria nazionale può cambiarli.',
        },
        {
          type: 'ul',
          items: [
            '“Avvisa l’assegnatario quando cambia lo stato del ticket”: a ogni cambio di stato (presa in carico, cambio manuale, stato scelto nel popup di invio, risoluzione, chiusura) parte un’email agli assegnatari, secondo le preferenze di ciascuno (subito, fascia oraria, riepilogo o nessuna). Chi fa il cambio non la riceve. Non scatta per trasferimenti, unioni, chiusure automatiche né quando una risposta porta da sola il ticket in lavorazione.',
            '“Avvisa il richiedente quando cambia lo stato”: l’associato (o il richiedente esterno) riceve un’email quando il ticket viene preso in carico, torna in lavorazione, aspetta una sua risposta o viene chiuso. Se cambi lo stato nel popup di invio, lo stato arriva nella stessa email della risposta. Non vale mai dal canale di smistamento e non ripete lo stesso stato a pochi secondi di distanza.',
            '“Mostra il nome del rappresentante che risponde”: l’associato vede “Nome Cognome · Canale” invece del solo nome del canale, nella scheda del ticket e nelle email. La foto resta nascosta.',
            '“Invia subito l’email a chi viene menzionato”: chi viene menzionato in una nota interna riceve l’email subito, anche se ha scelto la fascia oraria o il riepilogo. Chi ha scelto “nessuna email” non la riceve comunque.',
            '“Tutti i componenti possono approvare le bozze”: spento, le bozze le approvano solo i responsabili del canale e gli amministratori; acceso, anche ogni componente che può rispondere e ha il permesso di gestire i ticket della carica. Vale per tutte le bozze del canale, comprese quelle di chi è stato invitato; chi è invitato non approva mai.',
          ],
        },
        {
          type: 'tip',
          title: 'Dove si cambiano',
          text: 'Dall’ingranaggio “Impostazioni canali” nella barra, sezione “Notifiche e visualizzazione” di ogni canale. La modifica si salva subito al clic; se il salvataggio non riesce l’interruttore torna com’era e compare un avviso.',
        },
      ],
    },
    // TODO(T44): screenshot della veste v4 da rifare dopo la verifica visiva su
    // staging, da mettere in `frontend/public/guide/ticket/` e in
    // GUIDE_IMAGE_WHITELIST prima di citarli qui con un blocco `img`:
    //   v4-r01-coda.webp       lista con la barra v4 (percorso, contatori, strumenti) e la riga «I tuoi canali»
    //   v4-r02-scheda.webp     scheda con intestazione colorata e barra degli stati
    //   v4-r03-dettagli.webp   cassetto «Dettagli» con la pelle v4
    {
      id: 'veste-v4',
      title: 'Nuova veste v4 (anteprima)',
      blocks: [
        {
          type: 'p',
          text: '“Supporto” ha una nuova veste grafica, per ora in anteprima. La vede solo chi ha nella propria carica il permesso “Anteprima del nuovo Centro Ticket”; tutti gli altri continuano a vedere la pagina descritta nel resto di questa guida.',
        },
        {
          type: 'tip',
          title: 'Stesse funzioni, nuovo aspetto',
          text: 'Nella veste v4 non sparisce nessuna funzione e non cambiano i permessi: cambiano l’aspetto e il posto di alcune cose. Messaggi di conferma, avvisi ed etichette dei bottoni sono gli stessi.',
        },
        { type: 'p', text: 'La barra in alto:' },
        {
          type: 'ul',
          items: [
            'In cima alla pagina c’è una barra con il percorso “Area rappresentativa › Supporto” e il titolo “Ticket di supporto”. Resta visibile mentre scorri.',
            'Nella barra ci sono i contatori: Totali, Attivi e Da leggere sono cliccabili e filtrano la lista; In attesa, Risolti nel mese e 1ª risposta sono solo informativi.',
            'Guida all’uso, Base di conoscenza (se hai il permesso) e Notifiche email ora sono i bottoni rotondi a destra della barra, non più i link sopra la lista.',
            'Sotto la barra una riga ricorda i canali di cui fai parte (“I tuoi canali”). Il tema chiaro o scuro si cambia come sempre dalla barra dell’area associativa.',
          ],
        },
        {
          type: 'p',
          text: 'La lista resta una tabella con ricerca, filtri, viste salvate e schede come prima. Lo stato è un piccolo simbolo con il nome accanto, la priorità è fatta di barre e la nota interna da leggere è un rombo invece di un pallino: la legenda sotto la ricerca ricorda il significato. Il tuo avatar ha un anello verde.',
        },
        { type: 'p', text: 'La scheda del ticket:' },
        {
          type: 'ul',
          items: [
            'In alto ritrovi la stessa barra: “Supporto” nel percorso riporta alla lista, e un clic su un contatore torna alla lista già filtrata.',
            'L’intestazione ha il colore del canale: canale, numero, priorità, SLA e origine; l’oggetto con la matita (se puoi correggerlo); richiedente, assegnatari e data di apertura; i bottoni Prendi in carico, Rispondi, Risolvi, Dettagli e ⋯.',
            'Sotto l’intestazione una barra mostra a che punto è il ticket: Aperto, Preso in carico, In lavorazione, Risolto, Chiuso, con “In attesa associato” a parte. Serve solo a vedere: lo stato si cambia come prima, dal cassetto “Dettagli” o dal menu ⋯.',
            'Gli avvisi stanno sotto l’intestazione.',
            'Il menu ⋯ è diviso in gruppi con un titolo (Lavorazione, Richiedente, Conoscenza, Definitive); trovi solo le voci che i tuoi permessi consentono. La chiusura diretta, se ce l’hai, è in fondo nel gruppo Definitive.',
            'Conversazione e composer stanno nella stessa card. Il menu dell’assistente AI è diviso in “Sul ticket” (proporre una risposta, riassumere) e “Sulla bozza” (le rifiniture del testo); la proposta ricorda che non viene inviata da sola.',
          ],
        },
        { type: 'p', text: 'Il pannello laterale:' },
        {
          type: 'ul',
          items: [
            'Resta nel cassetto “Dettagli”, come prima, con le card Proprietà, Cronologia, Trasferimenti ed Email originale.',
            'In Proprietà lo stato ha il suo simbolo con sotto “Cambia stato…” e Applica; il canale ha il suo colore e, se hai il permesso, il bottone “Trasferisci”.',
            'La Cronologia mostra le tre voci più recenti: “Mostra tutte (N)” la apre per intero, “Mostra meno” la richiude.',
            'Il trasferimento a un altro canale apre il pannello rapido, sia da “Trasferisci a un gruppo” nel menu ⋯ sia dal bottone “Trasferisci” in Proprietà: in cima il riepilogo del ticket, poi scegli il canale fra le tessere, scrivi il motivo e confermi. Il canale in cui il ticket si trova già non compare.',
          ],
        },
        {
          type: 'warn',
          title: 'Anteprima',
          text: 'È una versione di prova: se qualcosa non torna, segnalalo alla segreteria indicando il numero del ticket e che cosa stavi facendo. Puoi sempre chiedere di tornare alla vista classica (vedi le domande frequenti).',
        },
      ],
    },
    {
      id: 'faq',
      title: 'Domande frequenti',
      blocks: [
        {
          type: 'p',
          text: 'Perché non vedo un ticket che un collega vede? Perché non sei componente di quel canale.',
        },
        {
          type: 'p',
          text: 'Come torno alla vista classica? La veste v4 dipende dal permesso “Anteprima del nuovo Centro Ticket” della tua carica: chiedi alla segreteria di toglierlo. L’amministratore lo toglie dai permessi della carica (vale per tutti quelli che la ricoprono); alla prossima apertura, o dopo aver ricaricato la pagina, ritrovi la vista classica con gli stessi ticket e le stesse funzioni.',
        },
        {
          type: 'p',
          text: 'Perché non vedo la nuova veste? Perché è in anteprima e si accende solo se la tua carica ha il permesso “Anteprima del nuovo Centro Ticket”, che va aggiunto a mano. Se te l’hanno appena dato, ricarica la pagina.',
        },
        {
          type: 'p',
          text: 'Ho corretto un messaggio: il richiedente riceve un avviso? No. La modifica vale solo per la piattaforma: l’email che gli era arrivata resta quella e non parte nessuna notifica. Lui vede soltanto la scritta “modificato” accanto alla data, e la cronologia del ticket registra la correzione.',
        },
        {
          type: 'p',
          text: 'Perché la lista non mostra i ticket risolti? Perché il filtro stato parte da “Attivi”. Cambialo in “Tutti gli stati”.',
        },
        {
          type: 'p',
          text: 'Che cosa conta “Da leggere”? I ticket attivi in cui c’è un messaggio del richiedente che nessuno del canale ha ancora aperto, oppure una nota interna di un collega che tu non hai ancora letto. Le note contano per ciascun operatore: aprire il ticket le toglie solo a te.',
        },
        {
          type: 'p',
          text: 'Posso chiudere un ticket a mano? Sì, se hai il permesso di chiusura diretta: “Chiudi ticket” dal menu ⋯, silenziosa e definitiva. Altrimenti mettilo su “Risolto”: dopo 14 giorni si chiude da solo.',
        },
        {
          type: 'p',
          text: 'Perché il richiedente ha ricevuto due email? Perché hai inviato la risposta e poi cambiato stato in un secondo momento. Scegli “Invia come risolto” nel popup di invio.',
        },
        {
          type: 'p',
          text: 'Il richiedente legge le mie note interne? No. La nota interna resta agli operatori del canale e non viene mai inviata.',
        },
        {
          type: 'p',
          text: 'Non vedo “Trasferisci a un gruppo”: perché? Perché il trasferimento ha un permesso suo. Chiedilo a chi gestisce i permessi dell’area associativa.',
        },
        {
          type: 'p',
          text: 'Dove trovo le proprietà del ticket? Nel cassetto “Dettagli”, che si apre dall’omonimo bottone o da “Cambia stato / priorità” nel menu ⋯.',
        },
        {
          type: 'p',
          text: 'Un ticket dice “Richiedente da attribuire”, o è nato a nome di chi ha inoltrato l’email: posso sistemarlo io? Solo se hai il permesso di cambio del richiedente: “Imposta richiedente” o “Cambia richiedente” dal menu ⋯. Il nuovo richiedente riceve l’email di apertura, il vecchio no, e i messaggi scritti a suo nome passano al nuovo. Senza permesso segnalalo alla segreteria.',
        },
        {
          type: 'p',
          text: 'Ho ricevuto “Il ticket è cambiato”: ho perso qualcosa? No. Qualcuno ha modificato il ticket mentre lavoravi: i dati si sono ricaricati, rileggi e ripeti l’azione se serve ancora.',
        },
        {
          type: 'p',
          text: 'Ho risolto un ticket ma non vedo la bozza nella base di conoscenza: perché? La bozza nasce solo se il canale ha scritto almeno una risposta pubblica e se l’assistente ha trovato una risposta riutilizzabile. Con il permesso di gestione puoi crearla dal menu ⋯ del ticket o scriverla a mano.',
        },
      ],
    },
  ],
};

export default guideTicketRappresentante;
